import React, { useEffect, useState, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { useWebviewMode } from '../../hooks/useWebviewMode';
import axios from 'axios';
import Swal from 'sweetalert2';
import './style.css';
import PlatformProfile from './PlatformProfile';
import ReviewsSettingsV2 from './ReviewsSettings';
import { useSelector, useDispatch } from 'react-redux';
import ReactSelect from 'react-select';
import { useUser } from '@clerk/clerk-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription
} from '../../components/ui/dialog';

import { authSelector } from '../../store/slices/auth';
import {
  selectedClientSelector,
  setSelectedClient
} from '../../store/slices/selectedClient';
import { showLoading, hideLoading } from '../../store/slices/loadingState';
import { clientsSelector, getClients } from '../../store/slices/clients';

import textRatingToNumber from '../Client/ReviewsSection/services/textRatingToNumber';

import {
  API_KEY,
  API_CLIENTS_NAME,
  API_REVIEW_UPDATE_MSG_REPLY,
  GET_REVIEWS_PER_CLIENT,
  API_REVIEWS_HANDLER,
  API_REVIEW_APPROVE,
  REVIEWS_GENERATE_REPLY_BY_AI,
  SEO_AUDIT_BASE
} from '../../utils';

import { accountManagers } from '../../utils';

import { Button } from '../../components/ui/button';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle
} from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '../../components/ui/select';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow
} from '../../components/ui/table';

import { Checkbox } from '../../components/ui/checkbox';

import {
  Star,
  Search,
  Pencil,
  Trash2,
  Check,
  X,
  Filter,
  User,
  Clock,
  RotateCcw,
  RefreshCw,
  Loader2,
  AlertCircle
} from 'lucide-react';

import { TruncatedText } from '../../components/TruncatedText';
import { getGoogleProfileInfo } from '../../components/GoogleBusinessProfile/services/index';

const Actions = ({ row, refresh, setLoading }) => {
  const reviews = useSelector((state) => state.reviews);
  const { selectedClient } = useSelector(selectedClientSelector);
  const { userInfo } = useSelector(authSelector);
  const dispatch = useDispatch();

  const getSeoApiConfig = useCallback(
    (baseConfig = {}) => {
      const headers = {
        ...(baseConfig.headers || {})
      };

      if (API_KEY) {
        headers['X-API-KEY'] = API_KEY;
      }

      if (userInfo?.accessToken) {
        headers.Authorization = `bearer ${userInfo.accessToken}`;
      }

      return {
        ...baseConfig,
        headers
      };
    },
    [userInfo?.accessToken]
  );

  // Modal states
  const [deleteConfirmModalOpen, setDeleteConfirmModalOpen] = useState(false);
  const [deleteSuccessModalOpen, setDeleteSuccessModalOpen] = useState(false);
  const [deleteErrorModalOpen, setDeleteErrorModalOpen] = useState(false);
  const [isApproving, setIsApproving] = useState(false);

  const handleCancel = async () => {
    dispatch({
      type: 'reviews/setEditable',
      payload: { id: row.id, editable: false }
    });
  };

  const handleClick = async () => {
    const latestDraftText = reviews?.editable?.message || row.msgReply || '';

    if (reviews?.editable?.id === row.id && reviews?.editable?.editable) {
      dispatch({
        type: 'reviews/setEditable',
        payload: { id: row.id, editable: false, message: latestDraftText }
      });

      if (
        reviews?.editable?.message &&
        reviews?.editable?.message.length > 0 &&
        reviews?.editable?.message !== row.msgReply
      ) {
        dispatch({
          type: 'reviews/save',
          payload: {
            loading: true,
            reviews: reviews?.reviews.map((review) =>
              review.id === row.id
                ? { ...review, msgReply: reviews?.editable?.message }
                : review
            )
          }
        });

        try {
          if (row._isNewAPI) {
            // Non-Google: save draft to DynamoDB via ms-seo-audit
            const SEO_API = SEO_AUDIT_BASE;

            const newMsgReply = reviews?.editable?.message;

            await axios.post(
              `${SEO_API}/reviews/respond`,
              {
                reviewId: row.reviewId,
                platform: row._platform,
                locationId: row.locationId,
                action: 'save_draft',
                finalText: newMsgReply,
                notifyEmail: userInfo?.email,
                reviewerName:
                  typeof (row.reviewer || row.reviewerName) === 'object'
                    ? row.reviewer?.displayName ||
                      row.reviewer?.name ||
                      'Customer'
                    : row.reviewer || row.reviewerName || 'Customer',
                rating: row.rating
              },
              getSeoApiConfig()
            );
            refresh();
          } else {
            // Google: use existing endpoint
            await axios.post(`${API_REVIEW_UPDATE_MSG_REPLY}/`, {
              id: row.id,
              msgReply: reviews?.editable?.message
            });
          }
        } catch (err) {
          console.error('Error saving reply:', err);
        } finally {
          dispatch({ type: 'reviews/save', payload: { loading: false } });
        }
      }
    } else if (!reviews?.editable?.editable) {
      dispatch({
        type: 'reviews/setEditable',
        payload: { id: row.id, editable: true, message: row.msgReply }
      });
    }
  };

  const handleApproved = async (value) => {
    if (isApproving) return;

    try {
      setIsApproving(true);

      // 1. Copy AI draft to clipboard
      let textToCopy = '';
      if (reviews?.editable?.id === row.id && reviews?.editable?.message) {
        textToCopy = reviews.editable.message;
      } else {
        textToCopy =
          row.msgReply ||
          row.response?.draftText ||
          row.response?.finalText ||
          '';
      }

      const finalText = (textToCopy || '').trim();

      if (textToCopy) {
        try {
          await navigator.clipboard.writeText(textToCopy);
        } catch (clipboardErr) {
          console.error('Failed to copy text to clipboard:', clipboardErr);
          // Optional: Notify user if clipboard copy fails, but continue
        }
      }

      // 2. Open review URL in a new tab for manual platforms
      const manualPlatforms = [
        'yelp',
        'tripadvisor',
        'opentable',
        'bbb',
        'bing',
        'angi'
      ];

      if (manualPlatforms.includes(row._platform)) {
        if (row.url) {
          window.open(row.url, '_blank', 'noopener,noreferrer');
        } else if (row._platform === 'yelp') {
          // Yelp Fallback
          const yelpId = selectedClient?.yelpId;
          const fallbackUrl = yelpId
            ? `https://www.yelp.com/biz/${yelpId}`
            : 'https://biz.yelp.com/';
          window.open(fallbackUrl, '_blank', 'noopener,noreferrer');
        } else if (row._platform === 'opentable') {
          // OpenTable Fallback
          const clientName =
            selectedClient?.clientName ||
            selectedClient?.mergeFields?.['~Name'] ||
            '';
          const fallbackUrl = clientName
            ? `https://www.opentable.com/r/${clientName}`
            : 'https://www.opentable.com/';
          window.open(fallbackUrl, '_blank', 'noopener,noreferrer');
        } else if (row._platform === 'bbb') {
          // BBB Fallback
          const clientName =
            selectedClient?.clientName ||
            selectedClient?.mergeFields?.['~Name'] ||
            '';
          const city = selectedClient?.mergeFields?.['~City'] || '';
          const fallbackUrl =
            clientName && city
              ? `https://www.bbb.org/search?find_text=${encodeURIComponent(
                  clientName
                )}&find_loc=${encodeURIComponent(city)}`
              : 'https://www.bbb.org/';
          window.open(fallbackUrl, '_blank', 'noopener,noreferrer');
        } else if (row._platform === 'bing') {
          // Bing Fallback
          const clientName =
            selectedClient?.clientName ||
            selectedClient?.mergeFields?.['~Name'] ||
            '';
          const city = selectedClient?.mergeFields?.['~City'] || '';
          const query = `${clientName} ${city}`.trim();
          const fallbackUrl = query
            ? `https://www.bing.com/maps?q=${encodeURIComponent(query)}`
            : 'https://www.bing.com/maps';
          window.open(fallbackUrl, '_blank', 'noopener,noreferrer');
        }
      }

      // 3. Proceed with backend approval
      if (row._isNewAPI) {
        // Non-Google: approve via ms-seo-audit
        const SEO_API = SEO_AUDIT_BASE;

        // manualPlatforms checked above
        const apiAction = manualPlatforms.includes(row._platform)
          ? 'mark_manual'
          : 'publish';

        const resolvedLocationId =
          row.locationId ||
          String(selectedClient?.id || selectedClient?.currentClientId || '');

        await axios.post(
          `${SEO_API}/reviews/respond`,
          {
            reviewId: row.reviewId,
            platform: row._platform,
            locationId: resolvedLocationId,
            action: apiAction,
            finalText,
            notifyEmail: userInfo?.email,
            reviewerName:
              typeof (row.reviewer || row.reviewerName) === 'object'
                ? row.reviewer?.displayName || row.reviewer?.name || 'Customer'
                : row.reviewer || row.reviewerName || 'Customer',
            rating: row.rating
          },
          getSeoApiConfig()
        );
        refresh();
      } else {
        // Google: use existing endpoint
        await axios.post(`${API_REVIEW_APPROVE}/`, {
          id: row.reviewId
        });
      }

      dispatch({
        type: 'reviews/approve',
        payload: reviews?.reviews.map((review) =>
          review.id === row.id ? { ...review, checked: value } : review
        )
      });

      Swal.fire({
        icon: 'success',
        title: 'Approved',
        text: 'Review response has been approved successfully.',
        timer: 1500,
        showConfirmButton: false
      });
    } catch (err) {
      console.error('Error approving review:', err);
      Swal.fire({
        icon: 'error',
        title: 'Approve Failed',
        text:
          err?.response?.data?.error ||
          err?.response?.data?.details ||
          err?.message ||
          'Failed to approve review response.'
      });
    } finally {
      setIsApproving(false);
    }
  };

  const handleDelete = () => {
    setDeleteConfirmModalOpen(true);
  };

  const confirmDelete = async () => {
    setDeleteConfirmModalOpen(false);
    setLoading(true);
    dispatch(showLoading());

    try {
      if (row._isNewAPI) {
        // Non-Google: delete via ms-seo-audit
        const SEO_API = SEO_AUDIT_BASE;
        await axios.delete(
          `${SEO_API}/reviews`,
          getSeoApiConfig({
            params: {
              locationId: row.locationId,
              reviewId: row.reviewId
            }
          })
        );
      } else {
        // Google: use existing endpoint
        await axios.post(API_REVIEWS_HANDLER, {
          review: {
            reviewId: row.reviewId,
            locationId: row.locationId,
            reviewer: row.reviewer,
            starRating: row.starRating
          },
          request: { sentBy: 'SUITE', operation: 'deny' }
        });
      }

      setDeleteSuccessModalOpen(true);
    } catch (err) {
      console.error('Error deleting review:', err);
      setDeleteErrorModalOpen(true);
    } finally {
      dispatch(hideLoading());
      setLoading(false);
      refresh();
    }
  };

  return (
    <div className="flex flex-col gap-2">
      {reviews?.editable?.editable && reviews?.editable?.id === row.id ? (
        <>
          <Button
            onClick={handleClick}
            variant="outline"
            size="sm"
            className="h-9 rounded-lg text-sm gap-2 justify-start text-green-600 border-green-200 hover:bg-green-50 hover:text-green-700">
            <Check className="h-4 w-4" />
            Confirm
          </Button>
          <Button
            onClick={handleCancel}
            variant="outline"
            size="sm"
            className="h-9 rounded-lg text-sm gap-2 justify-start text-destructive border-destructive/20 hover:bg-destructive/10 hover:text-destructive">
            <X className="h-4 w-4" />
            Cancel
          </Button>
        </>
      ) : reviews?.loading ? (
        <span className="text-sm text-muted-foreground">Loading...</span>
      ) : row?.checked ? (
        <span className="text-sm text-muted-foreground">Approved</span>
      ) : (
        <>
          <Button
            onClick={handleClick}
            variant="outline"
            size="sm"
            className="h-9 rounded-lg text-sm gap-2 justify-start">
            <Pencil className="h-4 w-4" />
            Edit
          </Button>
          <Button
            onClick={() => handleApproved(true)}
            disabled={isApproving}
            variant="outline"
            size="sm"
            className="h-9 rounded-lg text-sm gap-2 justify-start hover:bg-green-50 hover:text-green-700"
            style={{
              color: '#16a34a',
              borderColor: '#16a34a !important',
              borderWidth: '1px',
              borderStyle: 'solid'
            }}>
            <Check className="h-4 w-4" />
            {isApproving ? 'Approving...' : 'Approve'}
          </Button>
          <Button
            onClick={handleDelete}
            variant="outline"
            size="sm"
            className="h-9 rounded-lg text-sm gap-2 justify-start hover:bg-destructive/10"
            style={{
              color: '#ef4444',
              borderColor: '#ef4444 !important',
              borderWidth: '1px',
              borderStyle: 'solid'
            }}>
            <Trash2 className="h-4 w-4" />
            Delete
          </Button>
        </>
      )}

      {/* Dialog Modals */}
      <Dialog
        open={deleteConfirmModalOpen}
        onOpenChange={setDeleteConfirmModalOpen}>
        <DialogContent className="sm:max-w-md text-center py-10">
          <DialogHeader className="items-center">
            <DialogTitle className="text-2xl font-bold text-foreground">
              Delete Review
            </DialogTitle>
            <DialogDescription className="text-base text-muted-foreground mt-2">
              Are you sure you want to delete?
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-center gap-3 mt-4">
            <Button
              className="bg-primary hover:bg-primary/90 text-primary-foreground px-8 h-11 text-base font-semibold rounded-lg"
              onClick={confirmDelete}>
              Yes
            </Button>
            <Button
              variant="outline"
              onClick={() => setDeleteConfirmModalOpen(false)}
              className="px-8 h-11 text-base font-semibold rounded-lg">
              Cancel
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={deleteSuccessModalOpen}
        onOpenChange={setDeleteSuccessModalOpen}>
        <DialogContent className="sm:max-w-md text-center py-10">
          <DialogHeader className="items-center">
            <DialogTitle className="text-2xl font-bold text-foreground">
              Success!
            </DialogTitle>
            <DialogDescription className="text-base text-muted-foreground mt-2">
              Review Deleted ✅
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-center mt-4">
            <Button
              className="bg-primary hover:bg-primary/90 text-primary-foreground px-8 h-11 text-base font-semibold rounded-lg"
              onClick={() => setDeleteSuccessModalOpen(false)}>
              OK
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        open={deleteErrorModalOpen}
        onOpenChange={setDeleteErrorModalOpen}>
        <DialogContent className="sm:max-w-md text-center py-10">
          <DialogHeader className="items-center">
            <DialogTitle className="text-2xl font-bold text-foreground">
              Error!
            </DialogTitle>
            <DialogDescription className="text-base text-muted-foreground mt-2">
              Something went wrong ❌
            </DialogDescription>
          </DialogHeader>
          <div className="flex justify-center mt-4">
            <Button
              className="bg-primary hover:bg-primary/90 text-primary-foreground px-8 h-11 text-base font-semibold rounded-lg"
              onClick={() => setDeleteErrorModalOpen(false)}>
              OK
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

const Answer = ({ cell, row }) => {
  const reviews = useSelector((state) => state.reviews);
  const selectedClient = useSelector(selectedClientSelector);
  const editable =
    reviews?.editable?.editable && reviews?.editable?.id === row.id;

  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);

  const handleEditMessage = (msg) => {
    dispatch({ type: 'reviews/editMessage', payload: msg });
  };

  const handleAutoGenerate = async () => {
    try {
      setLoading(true);

      if (row._isNewAPI) {
        // Non-Google platforms: use ms-seo-audit AI draft endpoint
        const SEO_API = SEO_AUDIT_BASE;
        const res = await axios.post(`${SEO_API}/reviews/draft`, {
          review: {
            platform: row._platform,
            reviewId: row.reviewId,
            reviewerName: row.reviewer?.displayName || 'Customer',
            rating: row.starRating,
            text: row.rev_comment,
            locationId: row.locationId
          },
          businessName:
            selectedClient?.clientName ||
            selectedClient?.mergeFields?.['~Name'] ||
            '',
          brandTone: selectedClient?.brandTone || 'professional',
          maxCharacters: selectedClient?.maxAiCharacters
            ? Number(selectedClient.maxAiCharacters)
            : 500,
          additionalContext: selectedClient?.aiContext || ''
        });
        const draftText = res.data?.review?.response?.draftText;
        if (draftText) handleEditMessage(draftText);
      } else {
        // Google reviews: use existing endpoint
        const res = await fetch(REVIEWS_GENERATE_REPLY_BY_AI, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            review: row,
            name: row?.reviewer?.displayName,
            singlePerson: selectedClient?.selectedClient?.singlePerson
          })
        });
        const msg = await res.text();
        if (msg) handleEditMessage(msg);
      }
    } catch (err) {
      console.error('Error generating message', err);
    } finally {
      setLoading(false);
    }
  };

  if (editable) {
    return (
      <div className="relative">
        <textarea
          className="w-full p-2 text-sm border rounded-md min-h-[100px] focus:outline-none focus:ring-2 focus:ring-primary"
          placeholder="Edit the review answer message here"
          disabled={loading}
          value={reviews.editable.message || ''}
          onChange={(e) => handleEditMessage(e.target.value)}
        />

        {row?.rev_comment && (
          <button
            type="button"
            onClick={handleAutoGenerate}
            disabled={loading}
            className="absolute bottom-2 right-2 bg-blue-100 hover:bg-blue-200 rounded-full p-2 transition-colors"
            title="Auto Generate">
            <svg
              viewBox="0 0 32 32"
              xmlns="http://www.w3.org/2000/svg"
              className="h-4 w-4"
              style={{
                animation: loading ? 'spin 1s linear infinite' : 'none',
                transform: 'rotate(310deg)'
              }}>
              <path
                d="m16 0c8.836556 0 16 7.163444 16 16s-7.163444 16-16 16-16-7.163444-16-16 7.163444-16 16-16zm7.5013519 8h-5c-.5522847 0-1 .44771525-1 1v5c0 .5522847.4477153 1 1 1 .5522848 0 1-.4477153 1-1l.0011164-2.872584c1.5476338 1.1131888 2.4975317 2.9096298 2.4975317 4.872584 0 3.3137085-2.6862915 6-6 6s-6-2.6862915-6-6c0-2.6791615 1.771854-5.0121287 4.3028795-5.7568853.5298238-.1559011.832948-.71179106.6770468-1.24161488-.1559011-.52982382-.711791-.83294803-1.2416148-.67704688-3.3764809.99353263-5.7383115 4.10331096-5.7383115 7.67554706 0 4.418278 3.581722 8 8 8s8-3.581722 8-8c0-2.3423774-1.015734-4.5083081-2.7082286-6.00072306l2.2095805.00072306c.5522848 0 1-.44771525 1-1s-.4477152-1-1-1z"
                fill="#3766a8"
              />
            </svg>
          </button>
        )}
      </div>
    );
  }

  return (
    <TruncatedText
      text={cell}
      maxLength={60}
      className="text-sm text-muted-foreground"
    />
  );
};

const ReviewsV2 = () => {
  const [activeTab, setActiveTab] = useState('reviews');
  const location = useLocation();
  const dispatch = useDispatch();
  const { user } = useUser();

  const { userInfo } = useSelector(authSelector);
  const { selectedClient } = useSelector(selectedClientSelector);
  const { customerList } = useSelector(clientsSelector);

  // Sync activeTab with URL search params
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tabParam = params.get('tab');
    if (tabParam === 'platform-profile' || tabParam === 'settings') {
      setActiveTab(tabParam);
    } else {
      setActiveTab('reviews');
    }
  }, [location.search]);

  // Determine if user is superAdmin based on domain
  const userEmail = user?.primaryEmailAddress?.emailAddress || '';
  const isSuperAdmin =
    userEmail.endsWith('@adlerlocal.com') ||
    userEmail.endsWith('@samxlabs.com') ||
    userEmail.endsWith('@adlersocial.com');

  const isWebviewMode = useWebviewMode();

  const [showReplied] = useState(false);
  const [loading, setLoading] = useState(false);
  const [initialLoading, setInitialLoading] = useState(true);
  const [filter, setFilter] = useState(null);
  const [activePlatform, setActivePlatform] = useState('all'); // 'all', 'google', 'facebook'
  const [refresh, setRefresh] = useState(false);

  useEffect(() => {
    const handleExternalRefresh = () => {
      setRefresh((prev) => !prev);
    };

    window.addEventListener(
      'reviews-v2-refresh-requested',
      handleExternalRefresh
    );
    return () => {
      window.removeEventListener(
        'reviews-v2-refresh-requested',
        handleExternalRefresh
      );
    };
  }, []);

  // Handle change in client search select
  const handleChangeClient = async (selectedOption) => {
    if (!selectedOption) return;

    try {
      // Fetch complete client data from API to ensure all attributes are present
      const response = await axios.get(
        `${API_CLIENTS_NAME}${selectedOption.clientName}`,
        {
          headers: {
            'X-API-KEY': API_KEY,
            Authorization: `bearer ${userInfo.accessToken}`
          }
        }
      );

      if (response?.data) {
        // Update selected client in Redux with complete data including socialnetworks
        dispatch(setSelectedClient(response.data));
      } else {
        // Fallback to using the client from customerList if API call returns no data
        dispatch(setSelectedClient(selectedOption));
      }
    } catch (error) {
      console.error('Error fetching complete client data:', error);
      // Fallback to using the client from customerList if API call fails
      dispatch(setSelectedClient(selectedOption));
    }

    // Force full page reload to ensure all components refresh with new client data
    window.location.href = '/reviews-v2';
  };

  // Set label and value for search
  const getCustomerList = () => {
    if (!customerList?.length) {
      dispatch(getClients());
    }

    return customerList.map((customer) => ({
      ...customer,
      label: customer.clientName,
      value: customer.clientName
    }));
  };

  // helpers de fechas
  function getDaysAgoUnix() {
    const today = new Date();
    today.setDate(today.getDate() - 7);
    return Math.floor(today.getTime());
  }
  function getCurrentUnix() {
    return Math.floor(new Date().getTime());
  }
  function formatDateToInput(unixTimestamp) {
    const date = new Date(unixTimestamp);
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  const [dateFrom, setDateFrom] = useState(formatDateToInput(getDaysAgoUnix()));
  const [dateTo, setDateTo] = useState(formatDateToInput(getCurrentUnix()));
  const [starsFilter, setStarsFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');

  // Estado para el Google Average Rating (carga independiente)
  const [googleAverageRating, setGoogleAverageRating] = useState({
    loading: true,
    data: null
  });

  // Estado para Facebook reviews separado
  const [googleReviews, setGoogleReviews] = useState([]);
  const [facebookReviews, setFacebookReviews] = useState([]);
  const [facebookLoading, setFacebookLoading] = useState(false);

  const reviews = useSelector((state) => state?.reviews?.reviews);

  // Merge reviews based on active platform
  const mergedReviews = React.useMemo(() => {
    if (activePlatform === 'all') {
      return [...googleReviews, ...facebookReviews].sort(
        (a, b) => (b.createdTimeUnix || 0) - (a.createdTimeUnix || 0)
      );
    } else if (activePlatform === 'google') {
      return googleReviews;
    } else if (activePlatform === 'facebook') {
      return facebookReviews;
    }
    return reviews || [];
  }, [activePlatform, googleReviews, facebookReviews, reviews]);

  const setStateReviews = useCallback(
    (reviewsArr) => {
      dispatch({ type: 'reviews/setReviews', payload: reviewsArr });
    },
    [dispatch]
  );

  // Bulk Delete States
  const [selectedReviews, setSelectedReviews] = useState([]);
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);
  const [bulkDeleteModalOpen, setBulkDeleteModalOpen] = useState(false);
  const [bulkDeleteSuccessModalOpen, setBulkDeleteSuccessModalOpen] =
    useState(false);
  const [bulkDeleteErrorModalOpen, setBulkDeleteErrorModalOpen] =
    useState(false);

  // Cargar Google Average Rating de forma independiente
  useEffect(() => {
    const loadGoogleAverageRating = async () => {
      if (!selectedClient?.id) {
        setGoogleAverageRating({ loading: false, data: null });
        return;
      }

      setGoogleAverageRating({ loading: true, data: null });

      try {
        const profileInfo = await getGoogleProfileInfo(selectedClient.id);
        setGoogleAverageRating({
          loading: false,
          data: profileInfo.data?.reviews_info?.averageRating || null
        });
      } catch (error) {
        console.error('Error loading Google average rating:', error);
        setGoogleAverageRating({ loading: false, data: null });
      }
    };

    loadGoogleAverageRating();
  }, [selectedClient?.id]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const getGoogleReviews = async (queryParams) => {
    if (activePlatform !== 'all') {
      setStateReviews([]);
      setLoading(true);
      dispatch(showLoading());
    }

    axios
      .get(GET_REVIEWS_PER_CLIENT, {
        params: {
          id: selectedClient?.id,
          mode: 'recent'
        }
      })
      .then((res) => {
        let data = res.data?.reviews || [];

        data = data.map((item) => {
          item.id = item.reviewId;
          item._platform = 'google';
          item.source = 'google';

          const manager = accountManagers.find(
            (m) => m.id === item.accountOwner
          );
          if (manager) item.assignedTo = manager.full_name;

          return item;
        });

        const sortedData = data.sort(
          (a, b) => (b.createdTimeUnix || 0) - (a.createdTimeUnix || 0)
        );

        setGoogleReviews(sortedData);
        if (activePlatform === 'google') {
          setStateReviews(sortedData);
        }
        if (activePlatform !== 'all') {
          dispatch(hideLoading());
          setLoading(false);
        }
        setInitialLoading(false);
      })
      .catch((error) => {
        console.error('Error fetching Google reviews:', error);
        setGoogleReviews([]);
        if (activePlatform === 'google') {
          setStateReviews([]);
        }
        if (activePlatform !== 'all') {
          dispatch(hideLoading());
          setLoading(false);
        }
        setInitialLoading(false);
      });
  };

  /**
   * Fetch reviews from the ms-seo-audit DynamoDB API (all non-Google platforms).
   * Maps NormalizedReview shape → existing table row shape.
   */
  const SEO_AUDIT_API = SEO_AUDIT_BASE;

  const getSeoAuditRequestConfig = useCallback(
    (baseConfig = {}) => {
      const headers = {
        ...(baseConfig.headers || {})
      };

      if (API_KEY) {
        headers['X-API-KEY'] = API_KEY;
      }

      if (userInfo?.accessToken) {
        headers.Authorization = `bearer ${userInfo.accessToken}`;
      }

      return {
        ...baseConfig,
        headers
      };
    },
    [userInfo?.accessToken]
  );

  const getReviewsFromNewAPI = useCallback(
    async (platform, isSilent = false) => {
      setFacebookLoading(true);
      if (!isSilent && activePlatform !== 'all') {
        setStateReviews([]);
        setLoading(true);
        dispatch(showLoading());
      }

      try {
        const locationId =
          selectedClient?.id || selectedClient?.currentClientId || '';
        const params = { locationId };
        if (platform !== 'all') {
          params.platform = platform;
        }

        const res = await axios.get(
          `${SEO_AUDIT_API}/reviews`,
          getSeoAuditRequestConfig({ params })
        );
        const normalized = res.data?.reviews || [];

        // Map NormalizedReview → existing table row shape
        const mapped = normalized.map((r) => ({
          id: r.reviewId,
          reviewId: r.reviewId,
          rev_comment: r.text,
          starRating: r.rating, // already numeric 1-5
          reviewer: { displayName: r.reviewerName },
          createdTimeUnix: Math.floor(new Date(r.reviewDate).getTime() / 1000),
          _platform: r.platform,
          source: r.platform,
          _isNewAPI: true,
          response: r.response || { status: 'new' },
          msgReply: r.response?.draftText || r.response?.finalText || '',
          checked:
            r.response?.status === 'published' ||
            r.response?.status === 'manual_posting_required',
          rejected: r.response?.status === 'failed',
          locationId: r.locationId,
          locationName:
            r.platform.charAt(0).toUpperCase() + r.platform.slice(1),
          url: r.url
        }));

        const sorted = mapped.sort(
          (a, b) => b.createdTimeUnix - a.createdTimeUnix
        );

        setFacebookReviews(sorted);
        if (activePlatform === 'facebook') {
          setStateReviews(sorted);
        }
      } catch (error) {
        console.error('Error fetching reviews from new API:', error);
        setFacebookReviews([]);
        if (!isSilent && activePlatform === 'facebook') {
          setStateReviews([]);
        }
      } finally {
        setFacebookLoading(false);
        if (!isSilent && activePlatform !== 'all') {
          dispatch(hideLoading());
          setLoading(false);
          setInitialLoading(false);
        } else if (activePlatform === 'all') {
          setInitialLoading(false);
        }
      }
    },
    [
      selectedClient,
      dispatch,
      activePlatform,
      getSeoAuditRequestConfig,
      setStateReviews,
      SEO_AUDIT_API
    ]
  );

  /**
   * Fetch + ingest reviews from external platforms into DynamoDB,
   * then refresh the table from DynamoDB.
   */
  const [ingesting, setIngesting] = useState(false);

  const fetchAndIngestReviews = async (platform) => {
    if (platform === 'google') return; // Google uses its own legacy API

    setIngesting(true);
    const locationId =
      selectedClient?.id || selectedClient?.currentClientId || '';
    const clientName =
      selectedClient?.clientName ||
      selectedClient?.mergeFields?.['~Name'] ||
      '';
    const city = selectedClient?.mergeFields?.['~City'] || '';

    let isAsync = false;

    try {
      if (platform === 'all') {
        // Load saved platform settings from ReviewSettings table
        let savedPlatforms = {};
        try {
          const settingsRes = await axios.get(
            `${SEO_AUDIT_API}/reviews/settings`,
            getSeoAuditRequestConfig({ params: { locationId } })
          );
          savedPlatforms = settingsRes.data?.settings?.reviewPlatforms || {};
        } catch (e) {
          console.warn(
            'Could not load saved review settings, using client defaults:',
            e
          );
          savedPlatforms = selectedClient?.reviewPlatforms || {};
        }

        // Merge persisted settings with client-level defaults so fetch-all can
        // still include platforms not edited in the current settings UI.
        const mergedPlatforms = {
          ...(selectedClient?.reviewPlatforms || {}),
          ...(savedPlatforms || {})
        };

        // Use the batch ingest endpoint
        const platforms = {};

        if (
          mergedPlatforms.yelp?.businessId ||
          selectedClient?.yelpId ||
          clientName
        ) {
          platforms.yelp = {
            businessId:
              mergedPlatforms.yelp?.businessId || selectedClient?.yelpId || '',
            businessName: mergedPlatforms.yelp?.businessName || clientName,
            location: mergedPlatforms.yelp?.location || city
          };
        }

        // Facebook
        const fbPageIdAll =
          mergedPlatforms.facebook?.pageId ||
          selectedClient?.reviewPlatforms?.facebook?.pageId;
        const fbTokenAll =
          mergedPlatforms.facebook?.accessToken ||
          selectedClient?.reviewPlatforms?.facebook?.accessToken;

        if (fbPageIdAll && fbTokenAll) {
          platforms.facebook = {
            pageId: fbPageIdAll,
            accessToken: fbTokenAll
          };
        } else {
          // Let backend resolve Facebook credentials from facebook-dev table.
          platforms.facebook = {};
        }

        // Bing
        const bingQuery =
          mergedPlatforms.bing?.query || `${clientName} ${city}`.trim();
        if (bingQuery) {
          platforms.bing = { query: bingQuery };
        }

        // BBB
        const bbbBusinessUrl =
          mergedPlatforms.bbb?.businessUrl || mergedPlatforms.bbb?.url;
        if (bbbBusinessUrl) {
          platforms.bbb = {
            businessUrl: bbbBusinessUrl
          };
        }

        // TripAdvisor
        const tripAdvisorUrl =
          mergedPlatforms.tripadvisor?.tripAdvisorUrl ||
          mergedPlatforms.tripadvisor?.urlPath ||
          mergedPlatforms.tripadvisor?.url;
        if (tripAdvisorUrl) {
          platforms.tripadvisor = {
            tripAdvisorUrl
          };
        }

        // Angi
        const angiProfileUrl =
          mergedPlatforms.angi?.profileUrl || mergedPlatforms.angi?.url;
        if (angiProfileUrl) {
          platforms.angi = { profileUrl: angiProfileUrl };
        }

        // OpenTable
        const openTableRestaurantUrl =
          mergedPlatforms.opentable?.restaurantUrl ||
          mergedPlatforms.opentable?.url ||
          mergedPlatforms.opentable?.restaurantId;
        if (openTableRestaurantUrl) {
          platforms.opentable = {
            restaurantUrl: openTableRestaurantUrl
          };
        }

        if (Object.keys(platforms).length === 0) {
          Swal.fire({
            icon: 'warning',
            title: 'No Platforms Configured',
            text: 'No platform identifiers found for this client.'
          });
          setIngesting(false);
          return;
        }

        const resAll = await axios.post(
          `${SEO_AUDIT_API}/reviews/ingest/all`,
          {
            locationId,
            notifyEmail: userInfo?.email,
            platforms
          },
          getSeoAuditRequestConfig()
        );
        if (resAll.data && resAll.data.async) isAsync = true;
      } else {
        // Single platform ingest — load saved settings
        const payload = {
          platform,
          locationId,
          notifyEmail: userInfo?.email
        };
        let savedPlatforms = {};
        try {
          const settingsRes = await axios.get(
            `${SEO_AUDIT_API}/reviews/settings`,
            getSeoAuditRequestConfig({ params: { locationId } })
          );
          savedPlatforms = settingsRes.data?.settings?.reviewPlatforms || {};
        } catch (e) {
          console.warn(
            'Could not load saved review settings, using client defaults:',
            e
          );
          savedPlatforms = selectedClient?.reviewPlatforms || {};
        }

        const mergedPlatforms = {
          ...(selectedClient?.reviewPlatforms || {}),
          ...(savedPlatforms || {})
        };

        switch (platform) {
          case 'yelp':
            payload.businessId =
              mergedPlatforms.yelp?.businessId || selectedClient?.yelpId || '';
            if (!payload.businessId) {
              Swal.fire({
                icon: 'warning',
                title: 'Missing Yelp Info',
                text: 'No Yelp business ID found. Configure it in Review Settings.'
              });
              setIngesting(false);
              return;
            }
            break;
          case 'facebook':
            const fbPageId = mergedPlatforms.facebook?.pageId;
            const fbToken = mergedPlatforms.facebook?.accessToken;

            // Frontend may not have facebook credentials if managed in backend.
            // Backend will resolve them from facebook-dev when missing.
            if (fbPageId) payload.pageId = fbPageId;
            if (fbToken) payload.accessToken = fbToken;
            break;
          case 'bing':
            payload.query = mergedPlatforms.bing?.query || '';
            if (!payload.query) {
              Swal.fire({
                icon: 'warning',
                title: 'Missing Bing Query',
                text: 'No Bing search query configured. Configure it in Review Settings.'
              });
              setIngesting(false);
              return;
            }
            break;
          case 'tripadvisor':
            const taUrl =
              mergedPlatforms.tripadvisor?.tripAdvisorUrl ||
              mergedPlatforms.tripadvisor?.urlPath ||
              mergedPlatforms.tripadvisor?.url ||
              '';
            if (!taUrl) {
              Swal.fire({
                icon: 'warning',
                title: 'Missing TripAdvisor URL',
                text: 'No TripAdvisor page URL configured. Configure it in Connected Accounts.'
              });
              setIngesting(false);
              return;
            }
            payload.tripAdvisorUrl = taUrl;
            break;
          case 'angi':
            const angiUrl =
              mergedPlatforms.angi?.profileUrl ||
              mergedPlatforms.angi?.url ||
              '';
            if (!angiUrl) {
              Swal.fire({
                icon: 'warning',
                title: 'Missing Angi Profile URL',
                text: 'No specific Angi business profile URL provided in Review Settings.'
              });
              setIngesting(false);
              return;
            }
            payload.profileUrl = angiUrl;
            break;
          case 'bbb':
            const bbbUrl =
              mergedPlatforms.bbb?.businessUrl ||
              mergedPlatforms.bbb?.url ||
              '';
            if (!bbbUrl) {
              Swal.fire({
                icon: 'warning',
                title: 'Missing BBB Info',
                text: 'No BBB business URL provided. Configure it in Connected Accounts.'
              });
              setIngesting(false);
              return;
            }
            payload.businessUrl = bbbUrl;
            break;
          case 'opentable':
            payload.restaurantUrl =
              mergedPlatforms.opentable?.restaurantUrl ||
              mergedPlatforms.opentable?.url ||
              mergedPlatforms.opentable?.restaurantId ||
              '';
            if (!payload.restaurantUrl) {
              Swal.fire({
                icon: 'warning',
                title: 'Missing OpenTable URL',
                text: 'No OpenTable restaurant URL provided. Configure it in Connected Accounts.'
              });
              setIngesting(false);
              return;
            }
            break;
          default:
            break;
        }

        const resSingle = await axios.post(
          `${SEO_AUDIT_API}/reviews/ingest`,
          payload,
          getSeoAuditRequestConfig()
        );
        if (resSingle.data && resSingle.data.async) isAsync = true;
      }

      if (isAsync) {
        Swal.fire({
          icon: 'info',
          title: 'Syncing Data...',
          html: 'This usually takes 1-2 minutes.<br/><br/>You can keep working while we finish up in the background.',
          allowOutsideClick: false,
          showConfirmButton: true,
          confirmButtonText: 'Hide Loader & continue'
        });

        let attempts = 0;
        const pollInterval = setInterval(() => {
          attempts++;
          getReviewsFromNewAPI(platform, true);

          // Stop polling after ~150 seconds
          if (attempts >= 15) {
            clearInterval(pollInterval);
            if (
              Swal.isVisible() &&
              Swal.getTitle()?.textContent === 'Syncing Data...'
            ) {
              Swal.fire({
                icon: 'success',
                title: 'Check Finished',
                text: 'Finished waiting for background scraper. New reviews have been loaded if found.',
                timer: 3000,
                showConfirmButton: false
              });
            }
          }
        }, 10000);
      } else {
        Swal.fire({
          icon: 'success',
          title: 'Reviews Fetched!',
          text: 'Reviews have been fetched and saved. Loading...',
          timer: 2000,
          showConfirmButton: false
        });

        // Refresh from DynamoDB
        setTimeout(() => {
          getReviewsFromNewAPI(platform);
        }, 1000);
      }
    } catch (error) {
      console.error('Error ingesting reviews:', error);
      Swal.fire({
        icon: 'error',
        title: 'Fetch Failed',
        text:
          error?.response?.data?.error ||
          error.message ||
          'Failed to fetch reviews from platform.'
      });
    } finally {
      setIngesting(false);
    }
  };

  const renderStars = (count) => (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          className={`h-5 w-5 ${
            star <= count
              ? 'fill-yellow-400 text-yellow-400'
              : 'fill-muted text-muted'
          }`}
        />
      ))}
    </div>
  );

  const handleStatusChange = async (row, newStatus) => {
    const platform = row.platform || row._platform;

    // Optimistic update
    const updatedReviews = (reviews || []).map((r) => {
      if (r.reviewId === row.reviewId || r.id === row.id) {
        return {
          ...r,
          response: {
            ...(r.response || {}),
            status: newStatus
          },
          checked:
            newStatus === 'published' ||
            newStatus === 'manual_posting_required',
          rejected: newStatus === 'failed'
        };
      }
      return r;
    });
    setStateReviews(updatedReviews);

    if (row._isNewAPI) {
      try {
        await axios.post(
          `${SEO_AUDIT_API}/reviews/respond`,
          {
            reviewId: row.reviewId,
            platform: platform,
            locationId:
              row.locationId ||
              String(
                selectedClient?.id || selectedClient?.currentClientId || ''
              ),
            action: 'update_status',
            newStatus: newStatus,
            notifyEmail: userInfo?.email,
            reviewerName:
              typeof (row.reviewer || row.reviewerName) === 'object'
                ? row.reviewer?.displayName || row.reviewer?.name || 'Customer'
                : row.reviewer || row.reviewerName || 'Customer',
            rating: row.rating
          },
          getSeoAuditRequestConfig()
        );
        Swal.fire({
          icon: 'success',
          title: 'Status Updated',
          text: `Review status changed to ${newStatus.replace(/_/g, ' ')}`,
          timer: 1500,
          showConfirmButton: false
        });
      } catch (error) {
        console.error('Error updating status:', error);
        Swal.fire({
          icon: 'error',
          title: 'Update Failed',
          text: 'Failed to update review status.'
        });
        getReviewsFromNewAPI(activePlatform); // Revert on failure
      }
    } else {
      // Legacy Google reviews
      try {
        if (
          newStatus === 'published' ||
          newStatus === 'manual_posting_required'
        ) {
          await axios.post(`${API_REVIEW_APPROVE}/`, {
            id: row.reviewId || row.id
          });
          dispatch({
            type: 'reviews/approve',
            payload: (prevReviews) =>
              prevReviews.map((review) =>
                review.id === row.id
                  ? { ...review, checked: true, rejected: false }
                  : review
              )
          });
        }
        Swal.fire({
          icon: 'success',
          title: 'Status Updated',
          text: `Review status changed to ${newStatus.replace(/_/g, ' ')}`,
          timer: 1500,
          showConfirmButton: false
        });
      } catch (error) {
        console.error('Error updating legacy status:', error);
        Swal.fire({
          icon: 'error',
          title: 'Update Failed',
          text: 'Failed to update review status.'
        });
        // Can't easily revert state optimistic update here without full re-fetch
      }
    }
  };

  const getStatusBadge = (row) => {
    let currentStatus = row.response?.status;

    // Map legacy boolean states to string statuses if response.status is absent
    if (!currentStatus) {
      if (!row?.checked && !row.rejected) {
        currentStatus = 'new';
      } else if (row.rejected) {
        currentStatus = 'failed';
      } else if (row.checked) {
        currentStatus = 'published';
      } else {
        currentStatus = 'new';
      }
    }

    return (
      <Select
        value={currentStatus}
        onValueChange={(val) => handleStatusChange(row, val)}>
        <SelectTrigger className="h-8 w-[160px] text-xs bg-white border-gray-200">
          <SelectValue placeholder="Status" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="new">New</SelectItem>
          <SelectItem value="drafted">Drafted</SelectItem>
          <SelectItem value="needs_approval">Needs approval</SelectItem>
          <SelectItem value="published">Published</SelectItem>
          <SelectItem value="manual_posting_required">
            Manual posting
          </SelectItem>
          <SelectItem value="failed">Failed</SelectItem>
        </SelectContent>
      </Select>
    );
  };

  const DateFormat = (cell, row) => {
    if (cell && !row.rejected) {
      // createdTimeUnix is always in seconds for all platforms
      return (
        <span className="text-sm">
          {new Date(cell * 1000).toLocaleDateString()}
        </span>
      );
    }
    return <span className="text-sm">--</span>;
  };

  useEffect(() => {
    if (!selectedClient?.id && !selectedClient?.currentClientId) return; // wait for client

    // Start loading
    setLoading(true);
    dispatch(showLoading());

    const promises = [];

    if (activePlatform === 'google' || activePlatform === 'all') {
      promises.push(getGoogleReviews({ replied: showReplied, ...filter }));
    }
    if (activePlatform === 'facebook' || activePlatform === 'all') {
      promises.push(getReviewsFromNewAPI('facebook'));
    }

    // Wait for all promises and then stop loading
    Promise.allSettled(promises).finally(() => {
      setLoading(false);
      dispatch(hideLoading());
      setInitialLoading(false);
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filter, showReplied, activePlatform, refresh, selectedClient]);

  const reviewsSlice = useSelector((state) => state.reviews);

  // Apply local filters before mapping flags
  const filteredReviews = (mergedReviews || []).filter((row) => {
    // Platform Filter
    if (activePlatform === 'google' && row.source === 'facebook') {
      return false;
    }
    if (activePlatform === 'facebook' && row.source !== 'facebook') {
      return false;
    }
    // activePlatform === 'all' shows everything

    if (!filter) return true; // No filters applied

    let include = true;

    // Date Filters
    if (filter.dateFrom && row.createdTimeUnix < filter.dateFrom) {
      include = false;
    }
    if (filter.dateTo && row.createdTimeUnix > filter.dateTo) {
      include = false;
    }

    // Rating Filter
    if (filter.starRating !== undefined && filter.starRating !== 'all') {
      const numericTarget = parseInt(filter.starRating);
      const rowRating =
        row.source === 'facebook' || row._isNewAPI
          ? parseFloat(row.starRating)
          : textRatingToNumber(row.starRating);
      if (rowRating !== numericTarget) include = false;
    }

    // Status Filter — compare by response.status string directly
    if (filter.status) {
      const rowStatus =
        row.response?.status ||
        (row.rejected ? 'failed' : row.checked ? 'published' : 'new');
      if (rowStatus !== filter.status) include = false;
    }

    return include;
  });

  const dataWithFlags = filteredReviews.map((row) => ({
    ...row,
    _expanded: false,
    _isEditing:
      reviewsSlice.editable.editable && reviewsSlice.editable.id === row.id
  }));

  // Bulk Selection Handlers
  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedReviews(dataWithFlags.map((r) => r.id));
    } else {
      setSelectedReviews([]);
    }
  };

  const handleSelectOne = (checked, id) => {
    if (checked) {
      setSelectedReviews((prev) => [...prev, id]);
    } else {
      setSelectedReviews((prev) => prev.filter((reviewId) => reviewId !== id));
    }
  };

  const handleBulkDelete = () => {
    setBulkDeleteModalOpen(true);
  };

  const confirmBulkDelete = async () => {
    setBulkDeleteModalOpen(false);
    setIsBulkDeleting(true);
    dispatch(showLoading());

    const reviewsToDelete = dataWithFlags.filter((r) =>
      selectedReviews.includes(r.id)
    );
    let hasError = false;

    for (const row of reviewsToDelete) {
      try {
        if (row._isNewAPI) {
          await axios.delete(`${SEO_AUDIT_BASE}/reviews`, {
            params: {
              locationId: row.locationId,
              reviewId: row.reviewId
            }
          });
        } else {
          await axios.post(API_REVIEWS_HANDLER, {
            review: {
              reviewId: row.reviewId,
              locationId: row.locationId,
              reviewer: row.reviewer,
              starRating: row.starRating
            },
            request: { sentBy: 'SUITE', operation: 'deny' }
          });
        }
      } catch (err) {
        console.error(`Error deleting review ${row.id}:`, err);
        hasError = true;
      }
    }

    dispatch(hideLoading());
    setIsBulkDeleting(false);
    setSelectedReviews([]);
    setRefresh(!refresh);

    if (hasError) {
      setBulkDeleteErrorModalOpen(true);
    } else if (reviewsToDelete.length > 0) {
      setBulkDeleteSuccessModalOpen(true);
    }
  };

  // Use loading state for skeleton (both initial and filtering)
  const isLoadingData = loading || initialLoading;

  return (
    <div className="flex min-h-screen bg-muted/30">
      {/* Main Content */}
      <div className="flex-1">
        {/* Content Area */}
        <main className="p-6 lg:p-10">
          {/* Welcome Header */}
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 mb-8">
            <div>
              <h1 className="text-2xl lg:text-3xl font-bold text-foreground">
                Customer Reviews, {selectedClient?.clientName || ''}
              </h1>
              <p className="text-base text-muted-foreground mt-1">
                See what your customers are saying and respond to them
              </p>
            </div>
            <div className="flex items-center gap-4">
              {isSuperAdmin && !isWebviewMode && (
                <div className="hidden lg:block relative w-72">
                  <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground z-10" />
                  <ReactSelect
                    placeholder="Search"
                    onChange={(client) => handleChangeClient(client)}
                    options={getCustomerList()}
                    hideSelectedOptions={true}
                    controlShouldRenderValue={false}
                    components={{
                      DropdownIndicator: () => null,
                      IndicatorSeparator: () => null
                    }}
                    openMenuOnClick={false}
                    openMenuOnFocus={false}
                    styles={{
                      control: (provided, state) => ({
                        ...provided,
                        minHeight: '48px',
                        height: '48px',
                        borderRadius: '0.75rem',
                        borderWidth: '1px',
                        borderColor: state.isFocused
                          ? 'hsl(var(--primary))'
                          : 'hsl(var(--border))',
                        boxShadow: state.isFocused
                          ? '0 0 0 1px hsl(var(--primary))'
                          : 'none',
                        backgroundColor: '#ffffff',
                        paddingLeft: '2.5rem',
                        fontSize: '1rem',
                        '&:hover': {
                          borderColor: 'hsl(var(--primary))'
                        }
                      }),
                      placeholder: (provided) => ({
                        ...provided,
                        color: 'hsl(var(--muted-foreground))',
                        fontSize: '1rem'
                      }),
                      input: (provided) => ({
                        ...provided,
                        fontSize: '1rem',
                        color: 'hsl(var(--foreground))'
                      }),
                      menu: (provided) => ({
                        ...provided,
                        borderRadius: '0.75rem',
                        overflow: 'hidden',
                        boxShadow:
                          '0 10px 15px -3px rgb(0 0 0 / 0.1), 0 4px 6px -4px rgb(0 0 0 / 0.1)'
                      }),
                      option: (provided, state) => ({
                        ...provided,
                        backgroundColor: state.isFocused ? '#f1f5f9' : 'white',
                        color: state.isFocused ? '#1e293b' : '#64748b',
                        cursor: 'pointer',
                        padding: '12px 16px',
                        fontSize: '1rem'
                      })
                    }}
                  />
                </div>
              )}
              {activeTab === 'reviews' && (
                <>
                  {activePlatform === 'facebook' && (
                    <Button
                      size="lg"
                      onClick={() => fetchAndIngestReviews('facebook')}
                      disabled={ingesting}
                      style={{ paddingLeft: '2rem', paddingRight: '2rem' }}
                      className="h-14 text-lg font-semibold rounded-xl shadow-sm hover:shadow-md transition-all gap-3 flex items-center justify-center bg-primary hover:bg-primary/90 text-primary-foreground">
                      {ingesting ? (
                        <Loader2 className="h-5 w-5 animate-spin" />
                      ) : (
                        <RefreshCw className="h-5 w-5" />
                      )}
                      {ingesting ? 'Fetching...' : 'Fetch Reviews'}
                    </Button>
                  )}

                  {/* Bulk Delete Button */}
                  <Button
                    size="lg"
                    onClick={handleBulkDelete}
                    disabled={selectedReviews.length === 0 || isBulkDeleting}
                    style={{ paddingLeft: '2rem', paddingRight: '2rem' }}
                    className="h-14 text-lg font-semibold rounded-xl shadow-sm hover:shadow-md transition-all gap-3 flex items-center justify-center bg-red-600 hover:bg-red-700 text-white disabled:bg-gray-300 disabled:text-gray-500">
                    {isBulkDeleting ? (
                      <Loader2 className="h-5 w-5 animate-spin" />
                    ) : (
                      <Trash2 className="h-5 w-5" />
                    )}
                    {isBulkDeleting
                      ? 'Deleting...'
                      : `Delete Selected (${selectedReviews.length})`}
                  </Button>

                  {/* <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button
                        variant="outline"
                        size="icon"
                        className="h-14 w-14 rounded-xl shadow-sm hover:shadow-md transition-all relative">
                        <Bell className="h-5 w-5 text-gray-700" />
                        {dbNotifications.length > 0 && (
                          <span className="absolute top-3 right-3 flex h-3 w-3">
                            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-400 opacity-75"></span>
                            <span className="relative inline-flex rounded-full h-3 w-3 bg-red-500 border-2 border-white"></span>
                          </span>
                        )}
                      </Button>
                    </DropdownMenuTrigger>
                    {/* <DropdownMenuContent
                      align="end"
                      className="w-80 max-h-96 overflow-y-auto">
                      {dbNotifications.length > 0 ? (
                        dbNotifications.map((notif, idx) => (
                          <DropdownMenuItem
                            key={idx}
                            className="p-3 border-b last:border-0 hover:bg-gray-50 cursor-pointer">
                            <div className="flex flex-col w-full">
                              <p
                                className="text-sm text-gray-800 font-medium whitespace-normal"
                                dangerouslySetInnerHTML={{
                                  __html: notif.title
                                    ? notif.title.replace(
                                        /\[object Object\]/g,
                                        'a customer'
                                      )
                                    : 'New Notification'
                                }}
                              />
                              <p className="text-xs text-gray-500 mt-1">
                                {new Date(notif.createdAt).toLocaleString()}
                              </p>
                            </div>
                          </DropdownMenuItem>
                        ))
                      ) : (
                        <div className="p-4 text-center text-sm text-gray-500">
                          No notifications 🎉
                        </div>
                      )}
                    </DropdownMenuContent> */}
                  {/* </DropdownMenu> */}

                  {/* <Button
                    variant="outline"
                    size="lg"
                    onClick={() => setActiveTab('settings')}
                    style={{ paddingLeft: '2rem', paddingRight: '2rem' }}
                    className="h-14 text-lg font-semibold rounded-xl shadow-sm hover:shadow-md transition-all gap-3 flex items-center justify-center">
                    <Settings className="h-5 w-5" />
                    Settings
                  </Button> */}
                </>
              )}
            </div>
          </div>

          {/* Tab Content */}
          {activeTab === 'platform-profile' && <PlatformProfile />}

          {activeTab === 'settings' && <ReviewsSettingsV2 />}

          {activeTab === 'reviews' && (
            <>
              {/* Unified Platform Stats Card */}
              <Card className="rounded-xl sm:rounded-2xl border-2 border-border shadow-sm overflow-hidden mb-4 sm:mb-8">
                <div className="flex flex-col sm:flex-row sm:items-stretch divide-y sm:divide-y-0 sm:divide-x divide-border">
                  {/* Google Stats */}
                  {activePlatform !== 'facebook' && (
                    <div className="flex-1 flex items-center">
                      <div className="flex items-center gap-2 px-3 sm:px-4 shrink-0">
                        <span className="h-2 w-2 rounded-full bg-foreground/60" />
                        <span className="text-xs sm:text-sm font-semibold text-foreground">
                          Google
                        </span>
                      </div>
                      <div className="grid grid-cols-3 flex-1 divide-x divide-border">
                        <div className="flex items-center justify-center gap-2 py-2.5 sm:py-3 px-2">
                          <Check className="h-4 w-4 text-green-600 shrink-0" />
                          <div className="leading-tight">
                            {isLoadingData ? (
                              <>
                                <div className="h-5 w-6 bg-gray-200 rounded animate-pulse mb-0.5" />
                                <div className="h-3 w-14 bg-gray-200 rounded animate-pulse" />
                              </>
                            ) : (
                              <>
                                <p className="text-base sm:text-lg font-bold text-foreground leading-none">
                                  {googleReviews?.filter(
                                    (r) => r.checked && !r.rejected
                                  ).length || 0}
                                </p>
                                <p className="text-[10px] sm:text-xs text-muted-foreground">
                                  Approved
                                </p>
                              </>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center justify-center gap-2 py-2.5 sm:py-3 px-2">
                          <Clock className="h-4 w-4 text-yellow-600 shrink-0" />
                          <div className="leading-tight">
                            {isLoadingData ? (
                              <>
                                <div className="h-5 w-6 bg-gray-200 rounded animate-pulse mb-0.5" />
                                <div className="h-3 w-14 bg-gray-200 rounded animate-pulse" />
                              </>
                            ) : (
                              <>
                                <p className="text-base sm:text-lg font-bold text-foreground leading-none">
                                  {googleReviews?.filter(
                                    (r) => !r.checked && !r.rejected
                                  ).length || 0}
                                </p>
                                <p className="text-[10px] sm:text-xs text-muted-foreground">
                                  Pending
                                </p>
                              </>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center justify-center gap-2 py-2.5 sm:py-3 px-2">
                          <Star className="h-4 w-4 text-primary fill-primary shrink-0" />
                          <div className="leading-tight">
                            {googleAverageRating.loading ? (
                              <>
                                <div className="h-5 w-6 bg-gray-200 rounded animate-pulse mb-0.5" />
                                <div className="h-3 w-14 bg-gray-200 rounded animate-pulse" />
                              </>
                            ) : googleAverageRating.data !== null ? (
                              <>
                                <p className="text-base sm:text-lg font-bold text-foreground leading-none">
                                  {googleAverageRating.data.toFixed(1)}
                                </p>
                                <p className="text-[10px] sm:text-xs text-muted-foreground">
                                  Avg. Rating
                                </p>
                              </>
                            ) : (
                              <>
                                <p className="text-base sm:text-lg font-bold text-foreground leading-none">
                                  —
                                </p>
                                <p className="text-[10px] sm:text-xs text-muted-foreground">
                                  Avg. Rating
                                </p>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Facebook Stats */}
                  {activePlatform !== 'google' && (
                    <div className="flex-1 flex items-center">
                      <div className="flex items-center gap-2 px-3 sm:px-4 shrink-0">
                        <span className="h-2 w-2 rounded-full bg-foreground/60" />
                        <span className="text-xs sm:text-sm font-semibold text-foreground">
                          Facebook
                        </span>
                      </div>
                      <div className="grid grid-cols-3 flex-1 divide-x divide-border">
                        <div className="flex items-center justify-center gap-2 py-2.5 sm:py-3 px-2">
                          <Check className="h-4 w-4 text-green-600 shrink-0" />
                          <div className="leading-tight">
                            {facebookLoading || isLoadingData ? (
                              <>
                                <div className="h-5 w-6 bg-gray-200 rounded animate-pulse mb-0.5" />
                                <div className="h-3 w-14 bg-gray-200 rounded animate-pulse" />
                              </>
                            ) : (
                              <>
                                <p className="text-base sm:text-lg font-bold text-foreground leading-none">
                                  {facebookReviews?.filter(
                                    (r) => r.checked && !r.rejected
                                  ).length || 0}
                                </p>
                                <p className="text-[10px] sm:text-xs text-muted-foreground">
                                  Approved
                                </p>
                              </>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center justify-center gap-2 py-2.5 sm:py-3 px-2">
                          <Clock className="h-4 w-4 text-yellow-600 shrink-0" />
                          <div className="leading-tight">
                            {facebookLoading || isLoadingData ? (
                              <>
                                <div className="h-5 w-6 bg-gray-200 rounded animate-pulse mb-0.5" />
                                <div className="h-3 w-14 bg-gray-200 rounded animate-pulse" />
                              </>
                            ) : (
                              <>
                                <p className="text-base sm:text-lg font-bold text-foreground leading-none">
                                  {facebookReviews?.filter(
                                    (r) => !r.checked && !r.rejected
                                  ).length || 0}
                                </p>
                                <p className="text-[10px] sm:text-xs text-muted-foreground">
                                  Pending
                                </p>
                              </>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center justify-center gap-2 py-2.5 sm:py-3 px-2">
                          <Star className="h-4 w-4 text-primary fill-primary shrink-0" />
                          <div className="leading-tight">
                            {facebookLoading || isLoadingData ? (
                              <>
                                <div className="h-5 w-6 bg-gray-200 rounded animate-pulse mb-0.5" />
                                <div className="h-3 w-14 bg-gray-200 rounded animate-pulse" />
                              </>
                            ) : (
                              <>
                                <p className="text-base sm:text-lg font-bold text-foreground leading-none">
                                  {(() => {
                                    if (facebookReviews.length === 0)
                                      return '0.0';
                                    const sum = facebookReviews.reduce(
                                      (acc, r) =>
                                        acc + (parseFloat(r.starRating) || 0),
                                      0
                                    );
                                    return (
                                      sum / facebookReviews.length
                                    ).toFixed(1);
                                  })()}
                                </p>
                                <p className="text-[10px] sm:text-xs text-muted-foreground">
                                  Avg. Rating
                                </p>
                              </>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </Card>

              {/* Compact Filters with Platform Selector */}
              <div className="flex flex-col sm:flex-row sm:flex-wrap sm:items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Filter className="h-4 w-4" />
                  <span>Filters:</span>
                </div>
                <div className="flex items-center gap-2">
                  <Input
                    type="date"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                    className="h-9 flex-1 sm:w-36 sm:flex-none rounded-lg text-xs sm:text-sm"
                  />
                  <span className="text-muted-foreground text-xs sm:text-sm">
                    to
                  </span>
                  <Input
                    type="date"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                    className="h-9 flex-1 sm:w-36 sm:flex-none rounded-lg text-xs sm:text-sm"
                  />
                </div>
                <div className="flex items-center gap-2">
                  <Select value={starsFilter} onValueChange={setStarsFilter}>
                    <SelectTrigger className="h-9 flex-1 sm:w-32 sm:flex-none rounded-lg text-xs sm:text-sm">
                      <SelectValue placeholder="Rating" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Ratings</SelectItem>
                      <SelectItem value="5">5 Stars</SelectItem>
                      <SelectItem value="4">4 Stars</SelectItem>
                      <SelectItem value="3">3 Stars</SelectItem>
                      <SelectItem value="2">2 Stars</SelectItem>
                      <SelectItem value="1">1 Star</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="h-9 flex-1 sm:w-32 sm:flex-none rounded-lg text-xs sm:text-sm">
                      <SelectValue placeholder="Status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Statuses</SelectItem>
                      <SelectItem value="approved">Approved</SelectItem>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="rejected">Rejected</SelectItem>
                    </SelectContent>
                  </Select>
                  <Select
                    value={activePlatform}
                    onValueChange={setActivePlatform}>
                    <SelectTrigger className="h-9 flex-1 sm:w-36 sm:flex-none rounded-lg text-xs sm:text-sm">
                      <SelectValue placeholder="Platform" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Platforms</SelectItem>
                      <SelectItem value="google">Google Reviews</SelectItem>
                      <SelectItem value="facebook">Facebook Reviews</SelectItem>
                    </SelectContent>
                  </Select>
                  <Button
                    variant="ghost"
                    size="sm"
                    className="h-9 px-2 sm:px-3 text-xs sm:text-sm gap-1.5 shrink-0"
                    onClick={() => {
                      setFilter(null);
                      setDateFrom(formatDateToInput(getDaysAgoUnix()));
                      setDateTo(formatDateToInput(getCurrentUnix()));
                      setStarsFilter('all');
                      setStatusFilter('all');
                      setActivePlatform('all');
                    }}>
                    <RotateCcw className="h-3.5 w-3.5" />
                    <span className="hidden sm:inline">Clear</span>
                  </Button>
                </div>
              </div>

              {/* Reviews Table */}
              <Card className="rounded-2xl border-2 border-border shadow-sm overflow-hidden mt-8">
                <CardHeader className="pb-4">
                  <CardTitle className="text-lg font-medium">
                    Recent Reviews{' '}
                    {filteredReviews?.length > 0 &&
                      `(${filteredReviews.length})`}
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  {isLoadingData ? (
                    <div className="p-6 space-y-4">
                      {Array.from({ length: 5 }).map((_, idx) => (
                        <div
                          key={idx}
                          className="flex items-start gap-4 pb-4 border-b border-border last:border-0">
                          <div className="h-10 w-10 rounded-full bg-gray-200 animate-pulse" />
                          <div className="flex-1 space-y-2">
                            <div className="h-4 w-32 bg-gray-200 rounded animate-pulse" />
                            <div className="h-3 w-24 bg-gray-200 rounded animate-pulse" />
                            <div className="h-3 w-full bg-gray-200 rounded animate-pulse" />
                            <div className="h-3 w-3/4 bg-gray-200 rounded animate-pulse" />
                          </div>
                          <div className="flex gap-2">
                            <div className="h-5 w-5 bg-gray-200 rounded animate-pulse" />
                            <div className="h-5 w-5 bg-gray-200 rounded animate-pulse" />
                            <div className="h-5 w-5 bg-gray-200 rounded animate-pulse" />
                            <div className="h-5 w-5 bg-gray-200 rounded animate-pulse" />
                            <div className="h-5 w-5 bg-gray-200 rounded animate-pulse" />
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : dataWithFlags.length === 0 ? (
                    <div className="p-8 text-center text-muted-foreground">
                      No Reviews
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-muted/50">
                            <TableHead className="w-12 text-center py-4 px-2 border-r-0">
                              <Checkbox
                                checked={
                                  dataWithFlags.length > 0 &&
                                  selectedReviews.length ===
                                    dataWithFlags.length
                                }
                                onCheckedChange={handleSelectAll}
                                aria-label="Select all"
                              />
                            </TableHead>
                            <TableHead className="text-base font-semibold py-4 px-5 border-r-0">
                              Reviewer
                            </TableHead>
                            <TableHead className="text-base font-semibold py-4 border-r-0">
                              Platform
                            </TableHead>
                            <TableHead className="text-base font-semibold py-4 border-r-0">
                              Rating
                            </TableHead>
                            <TableHead className="text-base font-semibold py-4 min-w-[200px] border-r-0">
                              Comment
                            </TableHead>
                            <TableHead className="text-base font-semibold py-4 min-w-[220px] border-r-0">
                              Your Response
                            </TableHead>
                            <TableHead className="text-base font-semibold py-4 border-r-0">
                              Scheduled
                            </TableHead>
                            <TableHead className="text-base font-semibold py-4 border-r-0">
                              Status
                            </TableHead>
                            <TableHead className="text-base font-semibold py-4 px-5">
                              Actions
                            </TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {dataWithFlags.map((review) => {
                            const rating =
                              review.source === 'facebook' || review._isNewAPI
                                ? parseFloat(review.starRating)
                                : textRatingToNumber(review.starRating);

                            const platformLabel =
                              (review._platform || 'google')
                                .charAt(0)
                                .toUpperCase() +
                              (review._platform || 'google').slice(1);
                            const platformColors = {
                              google: 'bg-blue-100 text-blue-700',
                              yelp: 'bg-red-100 text-red-700',
                              facebook: 'bg-indigo-100 text-indigo-700',
                              tripadvisor: 'bg-green-100 text-green-700',
                              angi: 'bg-orange-100 text-orange-700',
                              bbb: 'bg-sky-100 text-sky-700',
                              bing: 'bg-teal-100 text-teal-700',
                              opentable: 'bg-rose-100 text-rose-700'
                            };

                            return (
                              <TableRow
                                key={review.id}
                                className={`align-top hover:bg-muted/30 ${
                                  selectedReviews.includes(review.id)
                                    ? 'bg-muted/50'
                                    : ''
                                }`}>
                                <TableCell className="py-5 px-2 text-center border-r-0">
                                  <Checkbox
                                    checked={selectedReviews.includes(
                                      review.id
                                    )}
                                    onCheckedChange={(checked) =>
                                      handleSelectOne(checked, review.id)
                                    }
                                    aria-label={`Select review from ${review.reviewer?.displayName}`}
                                  />
                                </TableCell>
                                <TableCell className="py-5 px-5 border-r-0">
                                  <div className="flex items-center gap-3">
                                    <div
                                      className="h-10 w-10 rounded-full flex items-center justify-center flex-shrink-0"
                                      style={{
                                        backgroundColor: '#3766A820'
                                      }}>
                                      <User
                                        className="h-5 w-5"
                                        style={{ color: '#3766A8' }}
                                      />
                                    </div>
                                    <div>
                                      <p className="font-medium text-base leading-tight mb-0">
                                        {review.reviewer?.displayName}
                                      </p>
                                      {review?.googleLink1 ||
                                      review?.googleLink2 ? (
                                        <a
                                          href={
                                            review?.googleLink2 ||
                                            review?.googleLink1
                                          }
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="text-sm hover:underline leading-tight"
                                          style={{ color: '#3766A8' }}>
                                          {review.locationName}
                                        </a>
                                      ) : (
                                        <p className="text-sm text-muted-foreground leading-tight">
                                          {review.locationName}
                                        </p>
                                      )}
                                      <p className="text-xs text-muted-foreground mt-0.5">
                                        {DateFormat(
                                          review.createdTimeUnix,
                                          review
                                        )}
                                      </p>
                                    </div>
                                  </div>
                                </TableCell>
                                <TableCell className="py-5 border-r-0">
                                  <Badge
                                    className={`${
                                      platformColors[
                                        review._platform || 'google'
                                      ] || 'bg-gray-100 text-gray-700'
                                    } text-xs px-2.5 py-1 rounded-full border-0 shadow-none font-medium`}>
                                    {platformLabel}
                                  </Badge>
                                </TableCell>
                                <TableCell className="py-5 border-r-0">
                                  {renderStars(rating)}
                                </TableCell>
                                <TableCell
                                  className="py-5 border-r-0"
                                  style={{
                                    minWidth: '200px',
                                    maxWidth: '300px',
                                    verticalAlign: 'top'
                                  }}>
                                  <TruncatedText
                                    text={review.rev_comment}
                                    maxLength={60}
                                  />
                                </TableCell>
                                <TableCell
                                  className="py-5 border-r-0"
                                  style={{
                                    minWidth: '220px',
                                    maxWidth: '320px',
                                    verticalAlign: 'top'
                                  }}>
                                  <Answer cell={review.msgReply} row={review} />
                                </TableCell>
                                <TableCell className="py-5 border-r-0">
                                  <div className="flex items-center gap-2 text-base">
                                    <Clock className="h-4 w-4 text-muted-foreground" />
                                    {DateFormat(review.schedule, review)}
                                  </div>
                                </TableCell>
                                <TableCell className="py-5 border-r-0">
                                  {getStatusBadge(review)}
                                </TableCell>
                                <TableCell className="py-5 px-5">
                                  <Actions
                                    row={review}
                                    refresh={() => setRefresh(!refresh)}
                                    setLoading={setLoading}
                                  />
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>
                  )}
                </CardContent>
              </Card>
              {/* Bulk Delete Dialogs */}
              <Dialog
                open={bulkDeleteModalOpen}
                onOpenChange={setBulkDeleteModalOpen}>
                <DialogContent className="sm:max-w-md text-center py-10">
                  <DialogHeader className="items-center">
                    <DialogTitle className="text-2xl font-bold text-foreground">
                      Delete Selected Reviews
                    </DialogTitle>
                    <DialogDescription className="text-base text-muted-foreground mt-2">
                      Are you sure you want to delete {selectedReviews.length}{' '}
                      selected review{selectedReviews.length > 1 ? 's' : ''}?
                      This action cannot be undone.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="flex justify-center gap-3 mt-4">
                    <Button
                      variant="destructive"
                      className="px-8 h-11 text-base font-semibold rounded-lg"
                      disabled={isBulkDeleting}
                      onClick={confirmBulkDelete}>
                      {isBulkDeleting ? (
                        <Loader2 className="h-5 w-5 animate-spin mr-2" />
                      ) : null}
                      Yes, Delete
                    </Button>
                    <Button
                      variant="outline"
                      onClick={() => setBulkDeleteModalOpen(false)}
                      disabled={isBulkDeleting}
                      className="px-8 h-11 text-base font-semibold rounded-lg">
                      Cancel
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>

              <Dialog
                open={bulkDeleteSuccessModalOpen}
                onOpenChange={setBulkDeleteSuccessModalOpen}>
                <DialogContent className="sm:max-w-md text-center py-10">
                  <DialogHeader className="items-center">
                    <div className="h-16 w-16 bg-green-100 rounded-full flex items-center justify-center mb-4 mx-auto">
                      <Check className="h-8 w-8 text-green-600" />
                    </div>
                    <DialogTitle className="text-2xl font-bold text-foreground">
                      Successfully Deleted!
                    </DialogTitle>
                    <DialogDescription className="text-base text-muted-foreground mt-2">
                      The selected reviews have been deleted.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="flex justify-center mt-4">
                    <Button
                      className="bg-primary hover:bg-primary/90 text-primary-foreground px-8 h-11 text-base font-semibold rounded-lg"
                      onClick={() => setBulkDeleteSuccessModalOpen(false)}>
                      Done
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>

              <Dialog
                open={bulkDeleteErrorModalOpen}
                onOpenChange={setBulkDeleteErrorModalOpen}>
                <DialogContent className="sm:max-w-md text-center py-10">
                  <DialogHeader className="items-center">
                    <div className="h-16 w-16 bg-red-100 rounded-full flex items-center justify-center mb-4 mx-auto">
                      <AlertCircle className="h-8 w-8 text-red-600" />
                    </div>
                    <DialogTitle className="text-2xl font-bold text-foreground">
                      Deletion Error
                    </DialogTitle>
                    <DialogDescription className="text-base text-muted-foreground mt-2">
                      Something went wrong while deleting some of the reviews.
                      Please try again.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="flex justify-center mt-4">
                    <Button
                      className="bg-primary hover:bg-primary/90 text-primary-foreground px-8 h-11 text-base font-semibold rounded-lg"
                      onClick={() => setBulkDeleteErrorModalOpen(false)}>
                      Close
                    </Button>
                  </div>
                </DialogContent>
              </Dialog>
            </>
          )}
        </main>
      </div>

      {/* Mantengo esto porque en Answer se usa animation: 'spin ...' */}
      <style>{`
        @keyframes spin {
          from { transform: rotate(310deg); }
          to { transform: rotate(670deg); }
        }
        
        /* Force button border colors and width */
        button.h-9.rounded-lg.text-sm.gap-2.justify-start:has(.lucide-check) {
          border-color: #86efac !important;
          border-width: 0.5px !important;
        }
        
        button.h-9.rounded-lg.text-sm.gap-2.justify-start:has(.lucide-trash-2) {
          border-color: #fca5a5 !important;
          border-width: 0.5px !important;
        }
        
        button.h-9.rounded-lg.text-sm.gap-2.justify-start:has(.lucide-pencil) {
          border-color: #cbd5e1 !important;
          border-width: 0.5px !important;
        }
      `}</style>
    </div>
  );
};

export default ReviewsV2;
